import {Box, Group, Text} from '@mantine/core';
import React from 'react';

import {ConjugationSite} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {conjugationSiteKey} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {AntibodyDomain} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';
import AdcConjugationSiteChip from 'molbio/adcs/registration-table/AdcConjugationSiteChip';
import getTestProps from 'test/lib/cypress/getTestProps';

/** The number of sites shown as chips; the rest are counted. */
export const SHOWN_CONJUGATION_SITES_LIMIT = 2;

interface ConjugationSiteChipsProps {
  sites: Array<ConjugationSite>;
  /** The antibody's domains, used to label the sites; see formatConjugationSite. */
  domains?: ReadonlyArray<AntibodyDomain> | null;
  /** Called when a site's chip is dismissed; the chips can't be dismissed without it. */
  onRemoveSite?: ((site: ConjugationSite) => void) | null;
}

/**
 * Chips for the first few of the given conjugation sites, with a count of the rest (e.g. "+3 more").
 */
export function ConjugationSiteChips({
  sites,
  domains = null,
  onRemoveSite = null,
}: ConjugationSiteChipsProps): JSX.Element | null {
  if (sites.length === 0) {
    return null;
  }
  const numHiddenSites = sites.length - SHOWN_CONJUGATION_SITES_LIMIT;
  return (
    // Wraps, so a cell too narrow for the row stacks the chips and count.
    <Group spacing="xs">
      {sites.slice(0, SHOWN_CONJUGATION_SITES_LIMIT).map((site) => (
        <AdcConjugationSiteChip
          key={conjugationSiteKey(site)}
          site={site}
          domains={domains}
          onRemove={
            onRemoveSite != null
              ? () => {
                  onRemoveSite(site);
                }
              : null
          }
        />
      ))}
      {numHiddenSites > 0 && (
        <Text
          size="xs"
          color="dimmed"
          {...getTestProps({component: 'ConjugationSiteChips', element: 'hiddenCount'})}
        >{`+${numHiddenSites} more`}</Text>
      )}
    </Group>
  );
}

interface AdcConjugationSitesDisplayProps {
  sites: Array<ConjugationSite>;
}

/**
 * Read-only display of a Conjugation sites cell. Reads nothing from the row, so it renders outside tables;
 * without the row's protein, sites are shown by their domain and position.
 */
export default function AdcConjugationSitesDisplay({sites}: AdcConjugationSitesDisplayProps): JSX.Element {
  return (
    <Box {...getTestProps({component: 'AdcConjugationSitesDisplay', element: 'root'})}>
      <ConjugationSiteChips sites={sites} />
    </Box>
  );
}
